/*       */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import LayerListHeader from 'app/components/molecules/Map/LayerList/LayerListHeader';
import { bind } from 'app/utils/decorators/decoratorUtils';
import { get } from 'app/utils/lo/lo';
import { getType } from 'app/config/typesConfig';
import VectorLayer from 'ol/layer/Vector';
import { __getMapLayers, getLayerType } from 'app/utils/maps/layer/layerUtils';
import { updateMapLayers } from 'store/actions/maps/situationalAwarenessActions';

const searchFeatures = (entityLayer, features, layerSearch) => {
    const entityType = getLayerType(entityLayer?.info || entityLayer?.values_?.attributes);
    if (!layerSearch) return features;
    const lowerCaseSearch = layerSearch.toLowerCase();

    if (entityType === 'event_type' || entityType === 'entity_type') {
        return features.filter(({ name, device }) => {
            const featureName = name || device?.name;
            return featureName?.toLowerCase()?.includes(lowerCaseSearch);
        });
    }
    return [];
};

class LayerList extends PureComponent {
    static propTypes = {
        mapId: PropTypes.string.isRequired,
        map: PropTypes.object.isRequired,
    };

    state = {
        layerModalOpen: false,
        layersSearch: '',
    };

    @bind
    toggleMapLayer(title, islayerVisible) {
        this.props.toggleMapLayer(this.props.name, title, islayerVisible);
    }

    @bind
    onLayersSearch(event) {
        const { value } = event.target || {};
        this.setState({ layersSearch: value || '' });
    }

    @bind
    getLayerFilters(entityType) {
        const layers = get(this.props, 'layers');
        if (!entityType) {
            return [];
        }
        const layer = (layers || []).find(({ title }) => getType(title) === getType(entityType));
        return (layer && layer.classifications) || [];
    }

    @bind
    getLayerFeatures(layer) {
        if (layer && layer instanceof VectorLayer) {
            return layer.getSource().getSource
                ? layer
                    .getSource()
                    .getSource()
                    .getFeatures()
                : layer.getSource().getFeatures();
        }
        return [];
    }
    
    @bind
    handleDragEnd(result) {
        const { destination, source } = result;
        
        // If dropped outside the list or in the same position, do nothing
        if (!destination || destination.index === source.index) {
            return;
        };
        
        const { mapData } = this.props;
        const mapLayers = __getMapLayers(mapData);

        const layersCopy = [...mapLayers];
        
        // Remove the dragged item from its original position
        const [movedLayer] = layersCopy.splice(source.index, 1);
        
        // Insert the dragged item at the new position
        layersCopy.splice(destination.index, 0, movedLayer);
        
        this.props.updateMapLayers(layersCopy);

        if (this.props.map && typeof this.props.map.reorderLayersByIdList === 'function') {
            const newOrderIds = layersCopy.map(l => l.id);
            this.props.map.reorderLayersByIdList(newOrderIds);
        }
    }

    @bind
    renderLayersList(layersSearch) {
        const { children, map, mapData, expandedItems, featuresMap } = this.props;
        const mapLayers = __getMapLayers(mapData);
     

        let filteredLayers = [...mapLayers];
        const filteredFeaturesMap = {};
    
        // Filter layers based on search
        if (layersSearch) {
            filteredLayers = mapLayers.filter(layer => 
                layer.name.toLowerCase().includes(layersSearch.toLowerCase()));
        }
    
        // Process features
        mapLayers.forEach(info => {
            const entityLayer = map.getEntityLayer(info?.id) || map.findLayerById(info?.id);

            const features = featuresMap[info?.id] || [];
            const filteredFeature = entityLayer ? searchFeatures(entityLayer, features, layersSearch) : features;
            filteredFeaturesMap[info?.id] = filteredFeature;
    
            if (layersSearch && filteredFeature?.length) {
                const index = filteredLayers.findIndex(item => item.id === info.id);
                const newInfo = { ...info, filteredFeature };
            
                if (index === -1) {
                    filteredLayers.push(newInfo);
                } else if (index !== -1) {
                    filteredLayers[index] = newInfo;
                }
            }
        });

        const listContainerStyle = {
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s ease',
            minHeight: '50px',
            height: '100%'
        };

        return (
            <Droppable droppableId="layer-list" type="LAYER">
                {(provided, snapshot) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                            ...listContainerStyle,
                            backgroundColor: snapshot.isDraggingOver ? 'rgba(0,0,0,0.05)' : 'transparent'
                        }}
                    >
                        {filteredLayers.length ? (
                            filteredLayers.map((item, index) => {
                                const modifiedItem = {
                                    ...item,
                                    type: item.type === 'cesium' ? 'system_cesium_layer' : item.type,
                                    iconName: item.type === 'cesium' ? 'globe' : item?.iconName,
                                    iconType: item.type === 'cesium' ? 'af' : item?.iconType,
                                    title: item.type === 'cesium' ? item?.name : item?.title,
                                    expanded: !!expandedItems[item?.name],
                                };
                                
                                return (
                                    <Draggable
                                        key={`layer-${modifiedItem.id || index}`}
                                        draggableId={`layer-${modifiedItem.id || index}`}
                                        index={index}
                                        type="LAYER"
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                {children(modifiedItem, index, filteredFeaturesMap, {
                                                    allMapLayers: filteredLayers
                                                })}
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })
                        ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                No Results
                            </div>
                        )}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        );
    }

    render() {
        const { layersSearch } = this.state;
        const { map, mapId, layersLoading, isLayerSidebar, mapData, entityTypes } = this.props;
        const mapLayers = __getMapLayers(mapData);
        
        const containerStyle = {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden' 
        };
        
        const scrollContainerStyle = {
            flex: 1,
            overflow: 'auto', 
            height: '100%'
        };
    
        return (
            <Fragment>
                <div style={containerStyle}>
                    <LayerListHeader
                        onLayersSearch={this.onLayersSearch}
                        value={layersSearch}
                        map={map}
                        mapId={mapId}
                        mapData={mapData}
                        layersLoading={layersLoading}
                        isLayerSidebar={isLayerSidebar}
                        classes={entityTypes}
                    />
                    {mapLayers && mapLayers.length > 0 && (
                        <div style={scrollContainerStyle}>
                            <DragDropContext onDragEnd={this.handleDragEnd}>
                                {this.renderLayersList(layersSearch)}
                            </DragDropContext>
                        </div>
                    )}
                </div>
            </Fragment>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    isLayerSidebar: state.sidebar.title === 'Layers',
    entityTypes: state.app.allPrimaryClasses.records || [],
    featuresMap: state.maps.situationalAwareness.map.featuresMap,
});

const mapDispatchToProps = {
    updateMapLayers
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(LayerList));
